import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Collapse
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, KeyboardArrowDown, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { styled } from '@mui/material/styles';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

// Styled components - Heron Power inspired minimalist design
const StyledAppBar = styled(AppBar)(({ theme, trigger, ishero }) => ({
  backgroundColor: trigger ? 'rgba(255, 255, 255, 0.98)' : 'transparent',
  boxShadow: 'none',
  backdropFilter: trigger ? 'blur(20px)' : 'none',
  borderBottom: trigger ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
  transition: 'all 0.3s ease-in-out',
  color: trigger ? '#000' : (ishero ? '#fff' : '#000'),
  '& .nav-link': {
    color: trigger ? '#000' : (ishero ? '#fff' : '#000'),
    '&:hover': {
      opacity: 0.6
    }
  }
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '20px',
  letterSpacing: '-0.02em',
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  alignItems: 'center'
}));

const NavLink = styled(Button)(({ theme, active }) => ({
  marginLeft: theme.spacing(4),
  color: 'inherit',
  fontWeight: 400,
  fontSize: '14px',
  letterSpacing: '0.02em',
  padding: '8px 0',
  minWidth: 'auto',
  position: 'relative',
  borderRadius: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: active ? '100%' : '0%',
    height: '1px',
    backgroundColor: 'currentColor',
    transition: 'width 0.3s ease'
  },
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.6,
    '&::after': {
      width: '100%'
    }
  },
  transition: 'opacity 0.2s ease-in-out'
}));

const CtaButton = styled(Button)(({ theme, trigger, ishero }) => ({
  backgroundColor: trigger ? '#000' : (ishero ? '#fff' : '#000'),
  color: trigger ? '#fff' : (ishero ? '#000' : '#fff'),
  border: 'none',
  marginLeft: theme.spacing(4),
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: 500,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: trigger ? '#333' : (ishero ? 'rgba(255,255,255,0.9)' : '#333'),
  },
  transition: 'all 0.2s ease-in-out'
}));

function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [solutionsAnchorEl, setSolutionsAnchorEl] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const { content } = useContent();
  const { site, header } = content;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = i18n.language;

  // Solutions 下拉菜单子项
  const solutionsSubmenu = [
    { title: 'Services', title_zh: '服务', path: '/#services' },
    { title: 'Products', title_zh: '产品', path: '/#products' },
  ];

  // Scroll trigger for changing header style
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50
  });

  // Solutions 下拉菜单处理
  const handleSolutionsOpen = (event) => {
    setSolutionsAnchorEl(event.currentTarget);
  };

  const handleSolutionsClose = () => {
    setSolutionsAnchorEl(null);
  };

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Toggle drawer
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Check if current path is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // 处理带有锚点的链接
    if (path.includes('#')) {
      const basePath = path.split('#')[0];
      // 如果是根路径 + 锚点的情况
      if (basePath === '' || basePath === '/') {
        return location.pathname === '/';
      }
      // 否则检查当前路径是否以基本路径开头
      return location.pathname.startsWith(basePath);
    }
    
    return location.pathname.startsWith(path);
  };

  // Map menu titles to translation keys
  const getTranslatedNavTitle = (title) => {
    switch(title) {
      case 'Home':
        return t('navigation.home');
      case 'About':
        return t('navigation.about');
      case 'Solutions':
        return currentLanguage === 'zh' ? '解决方案' : 'Solutions';
      case 'Services':
        return t('navigation.services');
      case 'Products':
        return t('navigation.products');
      case 'Projects':
        return t('navigation.projects');
      case 'Careers':
        return currentLanguage === 'zh' ? '招聘' : 'Careers';
      case 'Contact':
        return t('navigation.contact');
      default:
        return title;
    }
  };

  // 处理导航链接点击
  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    try {
      // 如果是锚点链接
      if (path.includes('#')) {
        const parts = path.split('#');
        const targetPage = parts[0] || '/';
        const targetId = parts[1];

        // 如果当前已在目标页面
        if (location.pathname === targetPage || 
           (location.pathname === '/' && targetPage === '/')) {
          // 平滑滚动到目标元素
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
          } else {
            console.warn(`Element with id '${targetId}' not found`);
            // 如果找不到元素，就滚动到页面顶部
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          // 跳转到目标页面
          navigate(targetPage);
          
          // 设置一个标记，表示需要在加载完成后滚动
          if (targetId) {
            sessionStorage.setItem('scrollToElementId', targetId);
          }
        }
      } else {
        // 正常导航
        navigate(path);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      // 发生错误时尝试进行常规导航
      try {
        window.location.href = path;
      } catch {
        // 最后手段：只做基本跳转
        navigate('/');
      }
    }

    // 关闭移动版抽屉
    setDrawerOpen(false);
  };

  // Check if we're on the home page hero section
  const isHeroSection = location.pathname === '/' && !trigger;

  return (
    <StyledAppBar position="fixed" trigger={trigger ? 1 : 0} ishero={isHeroSection ? 1 : 0}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ py: 2, minHeight: '80px' }}>
          {/* Logo */}
          <Box sx={{ flexGrow: 0, mr: 4, display: 'flex' }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo variant="h5">
                {site.logo ? (
                  <img
                    src={site.logo}
                    alt={site.title}
                    height="32"
                    style={{
                      marginRight: 8,
                      filter: isHeroSection && !trigger ? 'brightness(0) invert(1)' : 'none',
                      transition: 'filter 0.3s ease'
                    }}
                  />
                ) : (
                  <span style={{ fontWeight: 600, fontSize: '20px' }}>{site.title}</span>
                )}
              </Logo>
            </Link>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              {/* Home */}
              <NavLink
                component="a"
                href="/"
                onClick={(e) => handleNavClick(e, '/')}
                className="nav-link"
                active={location.pathname === '/' ? 1 : 0}
              >
                {getTranslatedNavTitle('Home')}
              </NavLink>

              {/* About */}
              <NavLink
                component="a"
                href="/#about"
                onClick={(e) => handleNavClick(e, '/#about')}
                className="nav-link"
              >
                {getTranslatedNavTitle('About')}
              </NavLink>

              {/* Solutions Dropdown */}
              <Box
                onMouseEnter={handleSolutionsOpen}
                onMouseLeave={handleSolutionsClose}
                sx={{ position: 'relative' }}
              >
                <NavLink
                  className="nav-link"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  {getTranslatedNavTitle('Solutions')}
                  <KeyboardArrowDown sx={{ fontSize: 18, ml: 0.5 }} />
                </NavLink>
                <Menu
                  anchorEl={solutionsAnchorEl}
                  open={Boolean(solutionsAnchorEl)}
                  onClose={handleSolutionsClose}
                  MenuListProps={{
                    onMouseLeave: handleSolutionsClose,
                  }}
                  sx={{
                    '& .MuiPaper-root': {
                      borderRadius: 0,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      minWidth: 180,
                      mt: 1,
                    }
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  {solutionsSubmenu.map((subItem) => (
                    <MenuItem
                      key={subItem.path}
                      onClick={(e) => {
                        handleNavClick(e, subItem.path);
                        handleSolutionsClose();
                      }}
                      sx={{
                        py: 1.5,
                        px: 3,
                        fontSize: '14px',
                        '&:hover': {
                          backgroundColor: '#f8f8f8',
                        }
                      }}
                    >
                      {currentLanguage === 'zh' ? subItem.title_zh : subItem.title}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Careers */}
              <NavLink
                component="a"
                href="/careers"
                onClick={(e) => handleNavClick(e, '/careers')}
                className="nav-link"
                active={location.pathname === '/careers' ? 1 : 0}
              >
                {getTranslatedNavTitle('Careers')}
              </NavLink>

              {/* Contact */}
              <NavLink
                component="a"
                href="/#contact"
                onClick={(e) => handleNavClick(e, '/#contact')}
                className="nav-link"
              >
                {getTranslatedNavTitle('Contact')}
              </NavLink>
            </Box>
          )}

          {/* CTA Button and Language Switcher */}
          {!isMobile && (
            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageSwitcher />
              <Box
                component="a"
                href={header.cta.path}
                onClick={(e) => handleNavClick(e, header.cta.path)}
                style={{ textDecoration: 'none' }}
              >
                <CtaButton
                  variant="contained"
                  trigger={trigger ? 1 : 0}
                  ishero={isHeroSection ? 1 : 0}
                >
                  {t('navigation.getQuote')}
                </CtaButton>
              </Box>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
              <LanguageSwitcher />
              <IconButton
                size="large"
                aria-label="menu"
                color="inherit"
                onClick={toggleDrawer(true)}
                sx={{
                  '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer - Full screen overlay style */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            height: '100vh',
            backgroundColor: '#000',
            color: '#fff',
            px: 3,
            py: 2,
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Logo variant="h5" sx={{ color: '#fff' }}>
            {site.logo ? (
              <img src={site.logo} alt={site.title} height="32" style={{ filter: 'brightness(0) invert(1)' }} />
            ) : (
              site.title
            )}
          </Logo>
          <IconButton onClick={toggleDrawer(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ mt: 4 }}>
          {/* Home */}
          <ListItem
            button
            component="a"
            href="/"
            onClick={(e) => handleNavClick(e, '/')}
            sx={{
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
            }}
          >
            <ListItemText
              primary={getTranslatedNavTitle('Home')}
              primaryTypographyProps={{ fontSize: '24px', fontWeight: 400, color: '#fff' }}
            />
          </ListItem>

          {/* About */}
          <ListItem
            button
            component="a"
            href="/#about"
            onClick={(e) => handleNavClick(e, '/#about')}
            sx={{
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
            }}
          >
            <ListItemText
              primary={getTranslatedNavTitle('About')}
              primaryTypographyProps={{ fontSize: '24px', fontWeight: 400, color: '#fff' }}
            />
          </ListItem>

          {/* Solutions with Submenu */}
          <ListItem
            button
            onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
            sx={{
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
            }}
          >
            <ListItemText
              primary={getTranslatedNavTitle('Solutions')}
              primaryTypographyProps={{ fontSize: '24px', fontWeight: 400, color: '#fff' }}
            />
            {mobileSubmenuOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
          </ListItem>
          <Collapse in={mobileSubmenuOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {solutionsSubmenu.map((subItem) => (
                <ListItem
                  key={subItem.path}
                  button
                  component="a"
                  href={subItem.path}
                  onClick={(e) => handleNavClick(e, subItem.path)}
                  sx={{
                    py: 1.5,
                    pl: 4,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
                  }}
                >
                  <ListItemText
                    primary={currentLanguage === 'zh' ? subItem.title_zh : subItem.title}
                    primaryTypographyProps={{ fontSize: '18px', fontWeight: 400, color: 'rgba(255,255,255,0.8)' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>

          {/* Careers */}
          <ListItem
            button
            component="a"
            href="/careers"
            onClick={(e) => handleNavClick(e, '/careers')}
            sx={{
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
            }}
          >
            <ListItemText
              primary={getTranslatedNavTitle('Careers')}
              primaryTypographyProps={{ fontSize: '24px', fontWeight: 400, color: '#fff' }}
            />
          </ListItem>

          {/* Contact */}
          <ListItem
            button
            component="a"
            href="/#contact"
            onClick={(e) => handleNavClick(e, '/#contact')}
            sx={{
              py: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': { backgroundColor: 'transparent', opacity: 0.6 }
            }}
          >
            <ListItemText
              primary={getTranslatedNavTitle('Contact')}
              primaryTypographyProps={{ fontSize: '24px', fontWeight: 400, color: '#fff' }}
            />
          </ListItem>
        </List>
        <Box sx={{ mt: 'auto', pb: 4 }}>
          <Button
            fullWidth
            variant="contained"
            component="a"
            href={header.cta.path}
            onClick={(e) => handleNavClick(e, header.cta.path)}
            sx={{
              py: 2,
              backgroundColor: '#fff',
              color: '#000',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }
            }}
          >
            {t('navigation.getQuote')}
          </Button>
        </Box>
      </Drawer>
    </StyledAppBar>
  );
}

export default Header; 